"use client";

import React, { useEffect, useRef, useState } from "react";
import { Ban, House, Shield, Trash2, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  title: "השגחה פרטית בבית חולים",
  description: "ליווי צמוד ומקצועי של מטפל מוסמך במהלך האשפוז, המעניק ביטחון ושקט נפשי למטופל ולבני המשפחה מסביב לשעון.",
  icon: Shield
}, {
  title: "טיפול סיעודי בבית",
  description: "סיוע יומיומי בפעולות שגרתיות, רחצה, הלבשה וניהול משק הבית, תוך שמירה על כבוד המטופל ואיכות חייו בסביבתו המוכרת.",
  icon: House
}, {
  title: "ליווי לאחר ניתוח",
  description: "תמיכה שיקומית ממוקדת הכוללת מעקב אחר החלמה, סיוע בניידות ומתן מענה לצרכים רפואיים ורגשיים בתקופה הקריטית.",
  icon: Ban
}, {
  title: "סיוע במיצוי זכויות",
  description: "ייעוץ והכוונה מקצועית מול המוסדות השונים לקבלת גמלת סיעוד, החזרים כספיים וזכויות רפואיות המגיעות לכם.",
  icon: Trash2
}, {
  title: "התאמת מטפל זר",
  description: "תהליך מקצועי של אבחון צרכים והתאמת מטפל סיעודי קבוע, כולל ליווי בירוקרטי מלא מול רשויות ההגירה והרווחה.",
  icon: Wrench
}];
export default function OurBirdRemovalServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="259664" id="שירותים-סיעודיים" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 max-w-7xl">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default mb-6 text-foreground">
            מעטפת שירותי סיעוד מקצועית
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            אנו מספקים פתרונות סיעוד מקיפים המותאמים אישית לצרכי המטופל, עם דגש על מקצועיות, חמלה וזמינות מיידית בכל רחבי הארץ.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.1
          });
          return <div key={i} data-index={i} ref={cardRef} className={`transition-all duration-700 ease-out ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className="h-full border-border shadow-md hover:shadow-lg transition-shadow duration-300 bg-card rounded-lg">
                  <CardHeader className="pb-4">
                    <div className="size-14 rounded-lg bg-accent flex items-center justify-center mb-4 text-primary">
                      <service.icon className="size-7" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-xl md:text-2xl font-default text-card-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base font-default text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="font-default text-base px-8 py-6 rounded-lg shadow-md hover:shadow-xl transition-all bg-primary text-primary-foreground">
            <Link to="/CaregiverServicePagesPage">לכל שירותי הסיעוד שלנו</Link>
          </Button>
        </div>
      </div>
    </section>;
}